import * as React from 'react'
import { ConnectError } from '@connectrpc/connect'
import * as Sentry from '@sentry/react'

import { isDiagnosticsEnabled, recordDiagnostic } from '@modules/diagnostics/store'
import { parseStructuredError } from '@modules/error/errorCodes'
import { useToast } from '@modules/shadcn/ui/Toast/use-toast'

type AsyncFunction<T extends unknown[]> = (...args: T) => Promise<void>
export const useSending = <T extends unknown[]>(fun: AsyncFunction<T>) => {
  const [sending, updateSending] = React.useState(false)
  // 連打ガードは ref で持つ。state の `sending` は closure に閉じ込められた render 時点の値なので、
  // 同一 render 中の連打では 2 回とも false を読んでしまい、ガードとして機能しない。
  // ref なら同期的に書き換わるため、1 回目の onSubmit が返る前の 2 回目を確実に弾ける。
  // （二重送信で「1 回目が成功してトークン/リソースを消費 → 2 回目が not_found」になる事故を防ぐ。issue #2646）
  const sendingRef = React.useRef(false)

  const onSubmit = async (...arg: T) => {
    if (sendingRef.current) return
    sendingRef.current = true
    updateSending(true)
    try {
      await fun(...arg)
    } finally {
      // 早期 return 時も含めて必ず戻す。戻し忘れるとボタンが永久に押せなくなる。
      sendingRef.current = false
      updateSending(false)
    }
  }

  return { sending, onSubmit }
}

export const useHandleError = () => {
  const { toast } = useToast()
  /**
   * `note` はトーストの説明に添える一文。失敗の結果として利用者に伝えたい事実（例: 失敗した
   * 取り込みでクレジットは消費されない。issue #2965）を、エラーの種類によらず出すときに使う。
   */
  const reportError = (e: unknown, note?: string) => {
    console.error(e)

    // 構造化エラー情報を取得
    const structuredError = parseStructuredError(e)

    // Connect Errorの場合
    if (e instanceof ConnectError) {
      // エラーコードに基づいたユーザーフレンドリーなメッセージを表示
      // Trace IDはローカル開発に加えて診断が有効な環境（STG）でも出す。
      // 以前はimport.meta.env.DEVだけを見ていたため、productionモードでビルドされる
      // STGでは出ず、検証担当者が手がかりを持ち帰れなかった
      const traceText =
        (import.meta.env.DEV || isDiagnosticsEnabled()) && structuredError.traceId
          ? `Trace ID: ${structuredError.traceId}`
          : undefined
      const description = [note, traceText].filter(Boolean).join(' ') || undefined

      toast({
        title: structuredError.message,
        description,
        variant: 'destructive',
      })

      // Sentryにも構造化エラー情報を送信
      Sentry.captureException(e, {
        extra: {
          errorCode: structuredError.code,
          traceId: structuredError.traceId,
          metadata: structuredError.metadata,
        },
      })
      return
    }

    // 通常のエラーの場合。
    // RPCの失敗はtransportのinterceptorが記録するので、ここではRPC以外の例外だけ記録する
    recordDiagnostic({
      kind: 'client',
      label: 'handleError',
      code: structuredError.code,
      message: structuredError.message,
    })
    Sentry.captureException(e)
    if (e instanceof Error) {
      toast({
        title: e.message,
        description: note,
        variant: 'destructive',
      })
    }
  }

  // 引数は1つに保つ。react-query の `onError: handleError` のようにそのまま渡す箇所があり、
  // `onError` は2つ目に mutation の変数を渡すので、2つ目の引数を足すとそれを取り違える。
  const handleError = (e: unknown) => reportError(e)
  /** トーストの説明に `note` を添える版（失敗時のクレジットの案内など）。`undefined` なら添えない。 */
  const handleErrorWithNote = (e: unknown, note: string | undefined) => reportError(e, note)

  return { handleError, handleErrorWithNote }
}

// @TOD: connectの形に合わせて書き直す
export const useSubmitHandleErrorForm = <T extends unknown[] = unknown[]>(
  fun: AsyncFunction<T>,
) => {
  const [formError, updateFormError] = React.useState<ConnectError | null>(null)
  const { handleError } = useHandleError()

  type FunArgs = Parameters<typeof fun>
  const onSubmit: AsyncFunction<FunArgs> = async (...arg: FunArgs) => {
    try {
      updateFormError(null)
      await fun(...arg)
    } catch (e) {
      handleError(e)
      if (e instanceof ConnectError) {
        updateFormError(e)
      }
    }
  }
  return { ...useSending(onSubmit), formError, updateFormError }
}
